import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService } from '../core/services/authentication.service';
import { HttpService } from '../core/services/http.service';
import { HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.css']
})
export class OnboardingComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;

  // Page controls
  showPasswordForm = false;
  showSuccessMessage = false;
  showEnterpriseForm = false;
  showLocationForm = false;

  // Reset password form fields
  temporaryPassword = '';
  newPassword = '';
  confirmPassword = '';
  customerName = this.authService.userObjectFromStorage?.userName || 'Customer';

  // Enterprise form fields
  enterpriseName = '';
  enterpriseStreet = '';
  enterpriseCity = '';
  enterpriseState = '';
  enterpriseZip = '';
  enterpriseTimezone = '';
  enterpriseLogoUrl = '';
  enterpriseLogo: File | null = null;

  enterpriseId: any = null;

  // Location form fields
  locationName = '';
  locationStreet = '';
  locationCity = '';
  locationState = '';
  locationZip = '';
  locationTimezone = '';

  uploading = false;
  timezones: any[] = [];
  locations: any[] = [];
  searchLocation = '';

  constructor(
    private httpService: HttpService,
    private authService: AuthenticationService,
    private router: Router
  ) {}

  ngOnInit() {
    this.fetchTimezones();
  }

  async fetchLocations(enterpriseId: number) {
    try {
      const locationsResponse = await this.httpService
        .get<any[]>(`Enterprise/${enterpriseId}/Location`)
        .toPromise();
      this.locations = locationsResponse || [];
    } catch (err) {
      console.error(err);
      alert('Error fetching timezones');
    }
  }

  async fetchTimezones() {
    try {
      const timezonesResponse = await this.httpService
        .get<any[]>(`Configuration/Timezone/ListServedTimezones`)
        .toPromise();
      this.timezones = timezonesResponse || [];
    } catch (err) {
      console.error(err);
      alert('Error fetching timezones');
    }
  }

  proceedOnboarding(): void {
    this.showPasswordForm = true;
  }

  resetPassword(): void {
    if (
      this.temporaryPassword === '' ||
      this.newPassword === '' ||
      this.confirmPassword === ''
    ) {
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      alert('New Password and Confirm Password do not match.');
      return;
    }

    this.httpService
      .post(
        `AppUser/ChangePassword`,
        {
          username: this.authService.userObjectFromStorage?.userName,
          new_password: this.newPassword,
          old_password: this.temporaryPassword
        },
        false
      )
      .subscribe({
        next: () => {
          this.showPasswordForm = false;
          this.showSuccessMessage = true;
        },
        error: () => {
          alert('Incorrect temporary password');
        }
      });
  }

  proceedToEnterprise(): void {
    this.showSuccessMessage = false;
    this.showEnterpriseForm = true;
  }

  proceedToLocation(): void {
    this.showEnterpriseForm = false;
    this.showLocationForm = true;
  }

  async handleImageUpload(event: any) {
    const file = event.target.files[0];
    if (file) {
      const imageFormData = new FormData();
      imageFormData.append('file', file);
      this.uploading = true;
      try {
        const headers = new HttpHeaders({
          accept: 'text/plain',
          'X-Auth': this.authService.userObjectFromStorage?.api_key || ''
        });
        const response = await this.httpService
          .post(`ImageUploader/upload`, imageFormData, false, {
            headers,
            responseType: 'text' as 'json' // Specify responseType as 'text'
          })
          .toPromise();
        this.enterpriseLogoUrl = response as string;
      } catch (error) {
        console.error('Error uploading image:', error);
      } finally {
        this.uploading = false;
      }
    }
  }

  uploadButtonClick() {
    this.fileInput.nativeElement.click();
  }

  async createEnterprise() {
    if (
      this.enterpriseName === '' ||
      this.enterpriseCity === '' ||
      this.enterpriseState === '' ||
      this.enterpriseZip === '' ||
      this.enterpriseTimezone === ''
    ) {
      return;
    }
    try {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Auth': this.authService.userObjectFromStorage?.api_key || ''
      });
      const response = await this.httpService
        .post<any>(
          `Enterprise`,
          {
            name: this.enterpriseName,
            jsonStreetAddress: [
              {
                street: this.enterpriseStreet,
                city: this.enterpriseCity,
                state: this.enterpriseState,
                zipcode: this.enterpriseZip
              }
            ],
            corpBranding: {
              images: [{ imageUrl: this.enterpriseLogoUrl }]
            },
            timezone: this.enterpriseTimezone
          },
          false,
          { headers }
        )
        .toPromise();
      this.enterpriseId = response as string;
      this.showEnterpriseForm = false;
      this.showLocationForm = true;
    } catch (err) {
      console.error(err);
      alert('Error creating enterprise');
    }
  }

  async createLocation() {
    // Validate form fields
    if (
      this.locationName.trim() === '' ||
      this.locationCity.trim() === '' ||
      this.locationState.trim() === '' ||
      this.locationZip.trim() === '' ||
      this.locationTimezone.trim() === ''
    ) {
      return;
    }

    const payload = {
      enterprise_Id: this.enterpriseId,
      name: this.locationName.trim(),
      jsonStreetAddress: [
        {
          street: this.locationStreet.trim(),
          city: this.locationCity.trim(),
          state: this.locationState.trim(),
          zipcode: this.locationZip.trim()
        }
      ],
      timezone: this.locationTimezone.trim()
    };

    try {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Auth': this.authService.userObjectFromStorage?.api_key || ''
      });

      await this.httpService
        .post(`Enterprise/${this.enterpriseId}/Location`, payload, false, {
          headers,
          responseType: 'json' // Ensure responseType matches expected response
        })
        .toPromise();

      // Clear form fields after successful creation
      this.locationName = '';
      this.locationCity = '';
      this.locationStreet = '';
      this.locationState = '';
      this.locationZip = '';
      this.locationTimezone = '';

      // Fetch the updated list of locations
      await this.fetchLocations(this.enterpriseId);
    } catch (err) {
      console.error('Error creating location:', err);
    }
  }

  goToEnterprise() {
    this.showEnterpriseForm = true;
    this.showLocationForm = false;
  }

  filteredLocations(): any[] {
    if (!this.searchLocation.trim()) {
      return this.locations;
    }
    const query = this.searchLocation.toLowerCase();
    return this.locations.filter((location) =>
      location.name.toLowerCase().includes(query)
    );
  }

  deleteLocation(location: any): void {
    const headers = new HttpHeaders({
      'X-Auth': this.authService.userObjectFromStorage?.api_key || ''
    });

    this.httpService
      .delete(`Location/${location.id}`, {
        headers
      })
      .subscribe({
        next: () => {
          this.locations = this.locations.filter((loc) => loc !== location);
        },
        error: (error) => {
          console.error('Error deleting location:', error);
        }
      });
  }

  completeOnboarding() {
    this.router.navigate(['/select-enterprise-location']);
  }

  getSelectedStep(): number {
    if (this.showPasswordForm) {
      return 0;
    } else if (this.showEnterpriseForm) {
      return 1;
    } else if (this.showLocationForm) {
      return 2;
    } else {
      return 0;
    }
  }
}
